import { useEffect } from 'react';
import { useDragDropManager } from 'react-dnd';

// While a native HTML5 drag is alive the browser keeps its drag-and-drop loop
// running and dispatches `drag`/`dragover` every ~350ms. The loop is closed by a
// `dragend` event, but that event is only dispatched on the dragged node itself:
// if that node is unmounted while the user still holds the item (the dashboard
// re-renders its orders on every refresh), `dragend` never reaches us and
// react-dnd stays in the "dragging" state forever. The row keeps its dragging
// styles and the drag preview stays frozen on the screen until some other drag
// is started.
//
// Mouse events are not dispatched while a native drag is running, so the first
// mouse event that arrives after the drag heartbeat went silent means the drag
// is really over and its leftover state can be closed by hand.
const DRAG_HEARTBEAT_TIMEOUT = 1000;

export const useStaleDragCleanup = () => {
  const manager = useDragDropManager();

  useEffect(() => {
    const monitor = manager.getMonitor();
    let lastDragActivity = 0;

    const handleOnDragActivity = () => {
      lastDragActivity = Date.now();
    };

    const handleOnDragEnd = () => {
      lastDragActivity = 0;
    };

    const handleOnMouseActivity = () => {
      if (!lastDragActivity || Date.now() - lastDragActivity < DRAG_HEARTBEAT_TIMEOUT) return;

      lastDragActivity = 0;
      if (monitor.isDragging()) {
        manager.getActions().endDrag();
      }
    };

    const listeners = [
      ['dragstart', handleOnDragActivity],
      ['drag', handleOnDragActivity],
      ['dragover', handleOnDragActivity],
      ['dragend', handleOnDragEnd],
      ['mousemove', handleOnMouseActivity],
      ['mousedown', handleOnMouseActivity],
      ['mouseup', handleOnMouseActivity],
    ];

    listeners.forEach(([type, listener]) => window.addEventListener(type, listener, true));

    return () => {
      listeners.forEach(([type, listener]) => window.removeEventListener(type, listener, true));
    };
  }, [manager]);
};
